import { useState } from "react";

import { useCreateGoogleFileSearchStore } from "@/queries/google-file-search-store";
import { displayToast, displayErrorToast } from "@/utils/toast";
import { ToastType } from "@/enums";

interface UseStoreCreationProps {
  onStoreCreated?: (storeName: string) => void;
  provider?: string;
}

export const useStoreCreation = ({
  onStoreCreated,
  provider = "google",
}: UseStoreCreationProps = {}) => {
  const [isCreating, setIsCreating] = useState(false);
  const [newStoreName, setNewStoreName] = useState("");

  const { mutate: createStore, isLoading: isCreatingStore } =
    useCreateGoogleFileSearchStore(provider);

  const handleCreateStore = () => {
    if (!newStoreName.trim()) {
      displayErrorToast("Please enter a store name");
      return;
    }

    createStore(
      { name: newStoreName },
      {
        onSuccess: data => {
          displayToast("File search store created successfully", ToastType.success);
          setIsCreating(false);
          setNewStoreName("");

          if (onStoreCreated && data?.store?.name) {
            onStoreCreated(data.store.name);
          }
        },
        onError: (error: unknown) => {
          const message =
            error instanceof Error ? error.message : "Failed to create file search store";
          displayErrorToast(message);
        },
      },
    );
  };

  const handleCancelCreation = () => {
    setIsCreating(false);
    setNewStoreName("");
  };

  return {
    isCreating,
    setIsCreating,
    newStoreName,
    setNewStoreName,
    isCreatingStore,
    handleCreateStore,
    handleCancelCreation,
  };
};
